/**
 * ============================================================================
 * EquipmentRow — Phase 6e frontend (Equipment & Validation Repository).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: render a single equipment <tr> in the workbench table. Combines
 *          the criticality chip and GAMP chip into one cell, surfaces the
 *          lifecycle status chip, and renders due-date cells with overdue
 *          highlight.
 *
 * A11Y: chips carry both a coloured background and a textual label so colour
 *  is not the sole signal (WCAG 1.4.1). Row actions are real buttons with
 *  visible text labels.
 * ============================================================================ */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import styles from './EquipmentWorkbench.module.css';
import type {
  EquipmentCriticality,
  EquipmentLifecycleStatus,
  EquipmentSummary,
  GampClass,
} from './useEquipmentQuery';

const CRIT_CLASS: Record<EquipmentCriticality, string> = {
  CRITICAL: styles.critCritical,
  MAJOR: styles.critMajor,
  MINOR: styles.critMinor,
  NON_GMP: styles.critNonGmp,
};

const GAMP_CLASS: Record<GampClass, string> = {
  GAMP5: styles.gamp5,
  GAMP4: styles.gamp4,
  GAMP3: styles.gamp3,
  GAMP1: styles.gamp1,
};

const LIFE_CLASS: Record<EquipmentLifecycleStatus, string> = {
  PLANNED: styles.lifeEarly,
  URS_APPROVED: styles.lifeEarly,
  DQ_APPROVED: styles.lifeEarly,
  FAT_PASSED: styles.lifeEarly,
  SAT_PASSED: styles.lifeEarly,
  IQ_PASSED: styles.lifeEarly,
  OQ_PASSED: styles.lifeEarly,
  PQ_PASSED: styles.lifeEarly,
  QUALIFIED: styles.lifeQualified,
  RESTRICTED: styles.lifeRestricted,
  RETIRED: styles.lifeRetired,
  DECOMMISSIONED: styles.lifeDecommissioned,
};

export interface EquipmentRowProps {
  equipment: EquipmentSummary;
  onOpen: (e: EquipmentSummary) => void;
}

function daysFromNow(iso: string | null): number | null {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  const now = Date.now();
  return Math.round((target - now) / (1000 * 60 * 60 * 24));
}

export function EquipmentRow({ equipment: e, onOpen }: EquipmentRowProps) {
  const { t, i18n } = useTranslation();
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language],
  );

  function dueClass(iso: string | null): string {
    const d = daysFromNow(iso);
    if (d == null) return '';
    if (d < 0) return styles.dueOverdue;
    if (d <= 7) return styles.dueSoon;
    return '';
  }

  function renderDueCell(iso: string | null) {
    if (!iso) return <span className={styles.subdued}>—</span>;
    return (
      <span className={`${styles.dueCell} ${dueClass(iso)}`}>
        <time dateTime={iso}>{dateFmt.format(new Date(iso))}</time>
      </span>
    );
  }

  return (
    <tr>
      <td>
        <code className={styles.tagMono}>{e.equipment_tag}</code>
      </td>
      <td>
        <button
          type="button"
          className={styles.linkBtn}
          onClick={() => onOpen(e)}
          aria-label={t('equipment.workbench.openAria', {
            defaultValue: 'Open equipment {{name}}',
            name: e.name,
          })}
        >
          <strong>{e.name}</strong>
        </button>
      </td>
      <td>{e.manufacturer ?? <span className={styles.subdued}>—</span>}</td>
      <td>
        <span className={styles.chipGroup}>
          <span
            className={`${styles.chip} ${CRIT_CLASS[e.criticality]}`}
            aria-label={t('equipment.criticality.aria', {
              defaultValue: 'Criticality: {{level}}',
              level: t(`equipment.criticality.${e.criticality}`, {
                defaultValue: e.criticality,
              }),
            })}
          >
            {t(`equipment.criticality.${e.criticality}`, {
              defaultValue: e.criticality,
            })}
          </span>
          <span
            className={`${styles.chip} ${GAMP_CLASS[e.gamp_class]}`}
            aria-label={t('equipment.gamp.aria', {
              defaultValue: 'GAMP class: {{cls}}',
              cls: t(`equipment.gamp.${e.gamp_class}`, {
                defaultValue: e.gamp_class,
              }),
            })}
          >
            {t(`equipment.gamp.${e.gamp_class}`, { defaultValue: e.gamp_class })}
          </span>
        </span>
      </td>
      <td>
        <span
          className={`${styles.chip} ${LIFE_CLASS[e.lifecycle_status]}`}
          aria-label={t('equipment.lifecycle.aria', {
            defaultValue: 'Lifecycle status: {{status}}',
            status: t(`equipment.lifecycle.${e.lifecycle_status}`, {
              defaultValue: e.lifecycle_status,
            }),
          })}
        >
          {t(`equipment.lifecycle.${e.lifecycle_status}`, {
            defaultValue: e.lifecycle_status,
          })}
        </span>
      </td>
      <td>{renderDueCell(e.next_calibration_at_utc)}</td>
      <td>{renderDueCell(e.next_pm_at_utc)}</td>
      <td>{renderDueCell(e.next_requalification_at_utc)}</td>
      <td>
        {e.location ?? (
          <span className={styles.subdued}>—</span>
        )}
      </td>
      <td>
        <Button
          size="sm"
          variant="tertiary"
          onClick={() => onOpen(e)}
          aria-label={t('equipment.workbench.viewAria', {
            defaultValue: 'View details for equipment {{name}}',
            name: e.name,
          })}
        >
          {t('common.view', { defaultValue: 'View' })}
        </Button>
      </td>
    </tr>
  );
}
